import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import TextField, { BaseTextFieldProps } from "@mui/material/TextField";
import * as CompanyEmailValidator from "company-email-validator";
import { debounce } from "lodash";
import { useState } from "react";

import EmailAddress, { InvalidEmailAddressError } from "../email-address";

const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    marginRight: 0,
    width: "100%",
  },
  marginBottom: theme.spacing(1.5),
  ":first-of-type": {
    marginRight: theme.spacing(1.5),
  },
}));

interface EmailInputProps extends BaseTextFieldProps {
  idPrefix?: string;
  className?: string;
  variant?: TextFieldVariants;
  disabled?: boolean;
  emailAddress?: EmailAddress;
  allowNonCompanyDomains?: boolean;
  onChange?: (emailAddress?: EmailAddress, error?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export default function EmailAddressInput(props: Readonly<EmailInputProps>) {
  const {
    idPrefix,
    className,
    variant,
    disabled,
    emailAddress,
    allowNonCompanyDomains,
    onChange,
    onKeyDown,
    ...otherProps
  } = props;

  const [emailFieldText, setEmailFieldText] = useState<string>(emailAddress?.value ?? "");
  const [emailFieldError, setEmailFieldError] = useState<string>("");

  function validateAndReport(emailValue?: string) {
    let email: EmailAddress | undefined;
    let newErrors: string = "";

    try {
      email = buildEmailAddress(emailValue, allowNonCompanyDomains);
      setEmailFieldError("");
    } catch (error) {
      if (error instanceof InvalidEmailAddressError) {
        newErrors = error.userFriendlyMessage;
        setEmailFieldError(newErrors);
      } else {
        throw error;
      }
    } finally {
      onChange?.(email, newErrors);
    }
  }

  return (
    <InputField
      id={idPrefix ? idPrefix + "-email-address-input" : "email-address-input"}
      className={className}
      label="Email Address"
      onChange={(event) => {
        const value = event.target.value;
        setEmailFieldText(value);
        debounce((value: string) => {
          validateAndReport(value);
        }, 500)(value);
      }}
      required
      disabled={disabled}
      value={emailFieldText}
      error={!!emailFieldError}
      helperText={emailFieldError}
      variant={variant}
      autoComplete="account-email"
      type="email"
      onKeyDown={onKeyDown}
      {...otherProps}
    />
  );
}

function buildEmailAddress(inputText?: string, allowNonCompanyDomains?: boolean): EmailAddress | undefined {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!inputText) {
    throw new InvalidEmailAddressError("Email address is required", inputText);
  }

  if (!emailRegex.test(inputText)) {
    throw new InvalidEmailAddressError(EmailAddress.VALID_EMAIL_FORMAT_MESSAGE, inputText);
  }

  if (
    !allowNonCompanyDomains &&
    !inputText.includes("@example.com") &&
    !CompanyEmailValidator.isCompanyEmail(inputText)
  ) {
    throw new InvalidEmailAddressError("Email address must be a company email domain", inputText);
  }
  return new EmailAddress(inputText);
}
